// @ts-strict-ignore
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useConfirmPayee } from 'hooks/queries/useConfirmPayee'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  PaymentMethods,
} from 'types/beneficiaries'
import { ConfirmPayeeBeneficiaryParams, ConfirmPayeeMatchStatuses } from 'types/payoutsV2'

interface UseConfirmBeneficiaryCalloutProps {
  paymentMethods?: string[]
  beneficiaryDetails: ConfirmPayeeBeneficiaryParams
  isPayout?: boolean
  isContact?: boolean
}

const VOP_ENABLED_PAYMENT_METHODS: string[] = [PaymentMethods.SEPA, PaymentMethods.SEPA_INSTANT]

export const useConfirmBeneficiaryCallout = ({
  paymentMethods = [],
  beneficiaryDetails,
  isPayout = true,
  isContact = false,
}: UseConfirmBeneficiaryCalloutProps) => {
  const { t } = useTranslation()

  const request = useMemo(() => {
    const req = {
      ...beneficiaryDetails,
    }

    delete req?.transferDestination

    return req
  }, [beneficiaryDetails])

  const isVopCheckEnabled = useMemo(() => {
    const isSwift = paymentMethods?.length === 1 && paymentMethods[0] === PaymentMethods.SWIFT
    const validPaymentMethods = paymentMethods?.filter(
      method => !!method && method !== PaymentMethods.SWIFT
    )
    const isEur = beneficiaryDetails?.currency === BeneficiaryDetailsCurrency.EUR

    if (!request || isSwift || !isEur) return false

    const isSepa = VOP_ENABLED_PAYMENT_METHODS.some(method => validPaymentMethods.includes(method))

    return !validPaymentMethods?.length || isSepa
  }, [request, beneficiaryDetails, isContact, paymentMethods])

  const {
    data: confirmPayeeData,
    error: confirmPayeeDataError,
    isFetching: isConfirmingPayee,
  } = useConfirmPayee({
    request,
    enabled: isVopCheckEnabled,
  })

  const accountNumberFormat = useMemo(() => {
    switch (beneficiaryDetails?.currency) {
      case BeneficiaryDetailsCurrency.EUR:
      case BeneficiaryDetailsCurrency.AED:
        return t('manageBeneficiaries.iban')
      case BeneficiaryDetailsCurrency.GBP:
      case BeneficiaryDetailsCurrency.ZAR:
        return t('manageBeneficiaries.accountNumber')
      case BeneficiaryDetailsCurrency.USD:
        return beneficiaryDetails?.transferDestination ===
          BeneficiaryTransferDestination.INTERNATIONAL
          ? t('manageBeneficiaries.accountNumberOrIban')
          : t('manageBeneficiaries.accountNumber')
      default:
        return t('manageBeneficiaries.accountNumberOrIban')
    }
  }, [beneficiaryDetails])

  const confirmationOfPayeeCallout = useMemo(() => {
    const isSwift =
      beneficiaryDetails?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL

    if (isSwift || (!confirmPayeeData && !confirmPayeeDataError)) {
      return
    }

    if (confirmPayeeDataError) {
      return isPayout
        ? t('wallets.fiatSend.verificationOfPayee.verificationNotPossibleTechnicalError', {
            accountNumberFormat,
          })
        : t('manageBeneficiaries.confirmationOfPayee.verificationNotPossibleTechnicalError', {
            accountNumberFormat,
          })
    }

    const translationBaseKey = isPayout
      ? 'wallets.fiatSend.verificationOfPayee'
      : 'manageBeneficiaries.confirmationOfPayee'

    switch (confirmPayeeData?.matchStatus) {
      case ConfirmPayeeMatchStatuses.MATCH:
        return t(`${translationBaseKey}.match`, { accountNumberFormat })
      case ConfirmPayeeMatchStatuses.PARTIAL_MATCH:
        return t(`${translationBaseKey}.closeMatch`, {
          actualName: confirmPayeeData?.actualName,
          accountNumberFormat,
        })
      case ConfirmPayeeMatchStatuses.NO_MATCH:
        return t(`${translationBaseKey}.noMatch`, { accountNumberFormat })
      case ConfirmPayeeMatchStatuses.TECHNICAL_ERROR:
        return t(`${translationBaseKey}.verificationNotPossibleTechnicalError`, {
          accountNumberFormat,
        })
      case ConfirmPayeeMatchStatuses.NOT_POSSIBLE:
        return t(`${translationBaseKey}.verificationNotPossible`, {
          accountNumberFormat,
        })
      case ConfirmPayeeMatchStatuses.NOT_REQUIRED:
      default:
        return
    }
  }, [confirmPayeeData, confirmPayeeDataError, accountNumberFormat, isPayout])

  const editBankAccount = useMemo(() => {
    const isNonEditableStatus =
      confirmPayeeData?.matchStatus === ConfirmPayeeMatchStatuses.MATCH ||
      confirmPayeeData?.matchStatus === ConfirmPayeeMatchStatuses.NOT_POSSIBLE

    if (isContact) {
      return !isNonEditableStatus
    }

    return isPayout && confirmPayeeData && !isNonEditableStatus
  }, [isPayout, confirmPayeeData, isContact])

  return useMemo(() => {
    return {
      confirmPayeeData,
      isConfirmingPayee,
      confirmationOfPayeeCallout,
      editBankAccount,
    }
  }, [confirmationOfPayeeCallout, isConfirmingPayee, confirmPayeeData, editBankAccount])
}
